import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import Header from './components/layout/Header.tsx';
import Footer from './components/layout/Footer.tsx';
import Home from './pages/Home.tsx';
import Deploy from './pages/Deploy.tsx';
import Plans from './pages/Plans.tsx';
import Roadmap from './pages/Roadmap.tsx';
import Contact from './pages/Contact.tsx';
import Account from './pages/Account.tsx';
import Auth from './pages/Auth.tsx';
import Success from './pages/Success.tsx';
import Cancel from './pages/Cancel.tsx';
import NotFound from './pages/NotFound.tsx';
import CookiePolicy from './pages/CookiePolicy.tsx';
import PrivacyPolicy from './pages/PrivacyPolicy.tsx';
import TermsOfService from './pages/TermsOfService.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { CookieConsentProvider } from './contexts/CookieConsentContext.tsx';
import { CookieBanner } from './components/ui/CookieBanner.tsx';
import Analytics from './components/Analytics.tsx';
import { blockExternalScripts } from './utils/cookieBlocker.ts';
import { useCookieAwareEffect } from './hooks/useCookieAwareEffect.ts';

const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin + '/auth',
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  }
};

const msalInstance = new PublicClientApplication(msalConfig);

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  useCookieAwareEffect();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const getCurrentPage = () => {
    switch (location.pathname) {
      case '/':
        return 'home';
      case '/deploy':
        return 'deploy';
      case '/plans':
        return 'plans';
      case '/roadmap':
        return 'roadmap';
      case '/contact':
        return 'contact';
      case '/account':
        return 'account';
      case '/auth':
        return 'auth';
      case '/success':
        return 'success';
      case '/cancel':
        return 'cancel';
      case '/cookie-policy':
        return 'cookie-policy';
      case '/privacy-policy':
        return 'privacy-policy';
      case '/terms-of-service':
        return 'terms-of-service';
      default:
        return 'home';
    }
  };

  const handleNavigate = (page: string) => {
    const path = page === 'home' ? '/' : `/${page}`;
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header currentPage={getCurrentPage()} onNavigate={handleNavigate} />
      <main>
        <Routes>
          <Route path="/" element={<Home onNavigate={handleNavigate} />} />
          <Route path="/deploy" element={<Deploy onNavigate={handleNavigate} />} />
          <Route path="/plans" element={<Plans onNavigate={handleNavigate} />} />
          <Route path="/roadmap" element={<Roadmap onNavigate={handleNavigate} />} />
          <Route path="/contact" element={<Contact onNavigate={handleNavigate} />} />
          <Route 
            path="/account" 
            element={
              <ProtectedRoute>
                <Account onNavigate={handleNavigate} />
              </ProtectedRoute>
            } 
          />
          <Route path="/auth" element={<Auth onNavigate={handleNavigate} />} />
          <Route 
            path="/success" 
            element={
              <ProtectedRoute>
                <Success onNavigate={handleNavigate} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/cancel" 
            element={
              <ProtectedRoute>
                <Cancel onNavigate={handleNavigate} />
              </ProtectedRoute>
            } 
          />
          <Route path="/cookie-policy" element={<CookiePolicy onNavigate={handleNavigate} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy onNavigate={handleNavigate} />} />
          <Route path="/terms-of-service" element={<TermsOfService onNavigate={handleNavigate} />} />
          <Route path="*" element={<NotFound onNavigate={handleNavigate} />} />
        </Routes>
      </main>
      <Footer onNavigate={handleNavigate} />
      <CookieBanner />
      <Analytics />
    </div>
  );
}

function App() {
  const [msalInitialized, setMsalInitialized] = useState(false);

  useEffect(() => {
    blockExternalScripts();
    
    msalInstance.initialize().then(() => {
      setMsalInitialized(true);
    }).catch((error) => {
      console.error('MSAL initialization failed');
      setMsalInitialized(true);
    });
  }, []);

  if (!msalInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <CookieConsentProvider>
      <MsalProvider instance={msalInstance}>
        <AuthProvider msalInstance={msalInstance}>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </MsalProvider>
    </CookieConsentProvider>
  );
}

export default App;
